import { ToastProvider } from '../design-system/Toast';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { ExperienceConfiguration, ThemeTokenSet } from '@ayumia/contracts';
import { ApiClient, type Fetch } from '../api/client';
import { apiGateway } from '../api/gateway';
import { createMockBookingService } from '../implementation/booking/booking-service';
import { BookingPage } from '../implementation/booking/BookingPage';
import { createDemoInterviewEngine } from '../implementation/interview/demo-engine';
import { InterviewPage } from '../implementation/interview/InterviewPage';
import { BackOfficePage } from '../implementation/backoffice/BackOfficePage';
import { DEMO_ENGAGEMENT_ID, ImplementationStore, seedDemoStore } from '../implementation/store';
import { AppShell } from '../shell/AppShell';
import { FullScreenContext } from './full-screen';
import { HomePage } from '../shell/HomePage';
import { totalOf } from '../shell/useBadges';
import { Empty, errorView, Loading } from '../states/States';
import { applyThemes } from '../theme/theme';
import { useLoad } from '../views/useLoad';
import { AppServicesContext, type AppServices } from '../views/ViewContext';
import { ViewPage } from '../views/ViewPage';
import { TenantPage } from '../admin/tenant/TenantPage';
import { AttentionPage } from '../work/AttentionPage';
import { InboxPage } from '../work/InboxPage';
import { MediaLibraryPage } from '../media/MediaLibraryPage';
import { TaskPage } from '../work/TaskPage';
import { sharedRunSandbox } from '../runtime/run-sandbox';
import { formatRoute, parseRoute, type Route } from './router';
import { leaveGuard } from './leave-guard';
import type { AuthenticationOutcome } from '@ayumia/contracts';
import { AuthClient } from '../api/auth-client';
import { PasswordResetPage, passwordLinkOf } from '../auth/PasswordResetPage';
import { SignInPage } from '../auth/SignInPage';
import { fetchSignInPresentation } from '../auth/sign-in-presentation';
import { AccountSecurityPage } from '../auth/AccountSecurityPage';
import { ThemesPage } from '../admin/ThemesPage';
import { ModelAtlasPage } from '../admin/model/ModelAtlasPage';
import { FormsStudioPage } from '../studio/forms/FormsStudioPage';
import { ViewsStudioPage } from '../studio/views/ViewsStudioPage';
import { GraphPage } from '../graph/GraphExplorer';
import { StudioClosed, pruneStudioNavigation, studioClosed, type Entitlements } from './studio-access';

/**
 * Application : une session, puis le cadre (`GetExperience`) et la page de la route. La session
 * s'ouvre par adresse et mot de passe (ADR-091) : le serveur la pose dans un cookie `HttpOnly` que
 * le script ne lit jamais ; un rechargement la retrouve par `GET /auth/me`. L'écran « Jeton de
 * session » (jeton `Bearer` collé, gardé en mémoire) n'existe plus qu'en développement, derrière
 * l'option explicite `VITE_AYUMIA_DEV_TOKEN_SIGNIN=true`. OIDC par tenant viendra plus tard.
 */

/**
 * Route du fragment d'URL. `replace` remplace l'entrée d'historique au lieu d'en ajouter une : la
 * frappe dans la recherche et « Afficher plus » ne remplissent pas l'historique (ADR-094).
 */
function useHashRoute(): [Route, (route: Route, options?: { replace?: boolean }) => void] {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  // Route affichée : le retour du navigateur sur une fiche gardée y ramène l'adresse.
  const shown = useRef(formatRoute(parseRoute(window.location.hash)));
  useEffect(() => {
    const onChange = (): void => {
      const target = parseRoute(window.location.hash);
      const guard = leaveGuard();
      if (guard !== null && formatRoute(target) !== shown.current) {
        // Une fiche ouverte ne se quitte que par « Sortir » : l'adresse revient sur elle, la fiche ouvre sa fenêtre.
        window.history.pushState(window.history.state, '', `${window.location.pathname}${window.location.search}${shown.current}`);
        guard(() => {
          shown.current = formatRoute(target);
          window.location.hash = formatRoute(target);
          setRoute(target);
        });
        return;
      }
      shown.current = formatRoute(target);
      setRoute(target);
    };
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  const navigate = useCallback((next: Route, options?: { replace?: boolean }): void => {
    const go = (): void => {
      shown.current = formatRoute(next);
      if (options?.replace === true) window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${formatRoute(next)}`);
      else window.location.hash = formatRoute(next);
      setRoute(next);
    };
    const guard = leaveGuard();
    if (guard !== null && formatRoute(next) !== shown.current) guard(go);
    else go();
  }, []);
  return [route, navigate];
}

/** Écran de développement seulement (ADR-091 §6) : un jeton de `dev-session`, porté en Bearer. */
function DevTokenSignIn({ onSession }: { onSession: (token: string) => void }) {
  const [token, setToken] = useState('');
  return (
    <main className="ay-signin">
      <form
        className="ay-card"
        onSubmit={(event) => {
          event.preventDefault();
          if (token.trim() !== '') onSession(token.trim());
        }}
      >
        <h1>AYUMIA</h1>
        <label className="ay-field">
          <span className="ay-field__label">Jeton de session</span>
          <input type="password" autoComplete="off" value={token} onChange={(event) => setToken(event.target.value)} />
        </label>
        <p className="ay-text ay-text--muted">En développement : jeton délivré par l’outil de provisionnement.</p>
        <button type="submit" className="ay-button">
          Ouvrir la session
        </button>
        <p className="ay-text ay-text--muted">
          Pas encore client ? <a href={formatRoute({ kind: 'booking' })}>Prendre rendez-vous</a>
        </p>
      </form>
    </main>
  );
}

function Workspace({ client, authClient, onSignOut, userLabel, entitlements = null }: { client: ApiClient; authClient: AuthClient; onSignOut: () => void; userLabel?: string; entitlements?: Entitlements }) {
  const [route, navigate] = useHashRoute();
  const [experience, reload] = useLoad<ExperienceConfiguration>('experience', async () => (await client.query<ExperienceConfiguration>('GetExperience')).data);
  // Chaîne appliquée sur place après un choix de thème ; sinon celle du chargement de l'expérience.
  const [chosenChain, setChosenChain] = useState<readonly ThemeTokenSet[] | null>(null);
  // Page plein écran (éditeur d'un studio) : la coquille se retire tant que la page le demande (ADR-145 §11).
  const [fullScreen, setFullScreen] = useState(false);
  const theme = useMemo(() => applyThemes(chosenChain ?? (experience.status === 'ready' ? experience.value.themes.map((entry) => entry.body) : [])), [experience, chosenChain]);
  const services = useMemo<AppServices | null>(
    () =>
      experience.status !== 'ready'
        ? null
        : {
            client,
            gateway: apiGateway(client),
            context: { ...experience.value.context, surface: 'web' },
            theme: theme.scope,
            navigate,
            components: new Map(),
            runSandbox: sharedRunSandbox,
            applyThemeChain: setChosenChain,
          },
    [client, experience, theme, navigate],
  );

  // Badge d'une entrée de menu : total de la requête nommée, autorisée au serveur comme toute requête.
  const loadBadge = useCallback(async (query: string) => totalOf(await client.query(query)), [client]);
  const style = theme.variables as CSSProperties;
  if (experience.status === 'loading') return <div className="ay-root" style={style}><Loading /></div>;
  if (experience.status === 'error') return <div className="ay-root" style={style}>{errorView(experience.error, reload)}</div>;

  // L'enregistrement ouvert et l'état de la liste vivent tous deux dans l'URL (ADR-094) : changer l'un garde l'autre.
  const routed = (recordId: string | undefined, list: string | undefined): Route | null => {
    const extra = { ...(recordId === undefined ? {} : { record_id: recordId }), ...(list === undefined || list === '' ? {} : { list }) };
    if (route.kind === 'view') return { kind: 'view', view_ref: route.view_ref, ...extra };
    if (route.kind === 'object') return { kind: 'object', object_type: route.object_type, ...extra };
    return null;
  };
  const withRecord = (recordId: string | undefined): void => {
    const next = routed(recordId, route.kind === 'view' || route.kind === 'object' ? route.list : undefined);
    if (next !== null) navigate(next);
  };
  const withList = (list: string, options: { replace: boolean }): void => {
    const next = routed(route.kind === 'view' || route.kind === 'object' ? route.record_id : undefined, list);
    if (next !== null) navigate(next, options);
  };

  // Le nom affiché vient de la connexion ou du contexte, jamais de l'identifiant (ADR-092 §6).
  const displayName = userLabel ?? experience.value.context.identity_display_name;
  const productName = experience.value.themes.find((entry) => entry.body.brand?.product_name !== undefined)?.body.brand?.product_name;
  // Studios fermés au tenant (ADR-148) : masqués du menu, message à l'arrivée par l'adresse. Présentation seulement.
  const navigation = pruneStudioNavigation(experience.value.navigation.body, entitlements);
  let page;
  // Pas de surtitre : le fil d'Ariane montre déjà le module ; l'en-tête porte le titre et la description de la vue.
  if (studioClosed(route, entitlements)) page = <StudioClosed />;
  else if (route.kind === 'view') page = <ViewPage key={route.view_ref} source={{ view_ref: route.view_ref }} {...(route.record_id === undefined ? {} : { recordId: route.record_id })} onRecordChange={withRecord} listState={route.list ?? ''} onListStateChange={withList} />;
  else if (route.kind === 'object') page = <ViewPage key={route.object_type} source={{ object_type: route.object_type }} {...(route.record_id === undefined ? {} : { recordId: route.record_id })} onRecordChange={withRecord} listState={route.list ?? ''} onListStateChange={withList} />;
  else if (route.kind === 'inbox') page = <InboxPage />;
  else if (route.kind === 'task') page = <TaskPage key={route.work_item_id} workItemId={route.work_item_id} />;
  else if (route.kind === 'tenant') page = <TenantPage tab={route.tab} />;
  else if (route.kind === 'attention') page = <AttentionPage onOpenTask={(workItemId) => navigate({ kind: 'task', work_item_id: workItemId })} />;
  else if (route.kind === 'account') page = <AccountSecurityPage client={authClient} />;
  else if (route.kind === 'themes') page = <ThemesPage />;
  else if (route.kind === 'media') page = <MediaLibraryPage key={route.library} library={route.library} {...(route.folder_id === undefined ? {} : { folderId: route.folder_id })} />;
  else if (route.kind === 'graph') page = <GraphPage route={route} />;
  else if (route.kind === 'model') page = <ModelAtlasPage {...(route.object_type === undefined ? {} : { objectType: route.object_type })} />;
  else if (route.kind === 'forms') page = <FormsStudioPage key={route.artifact_id ?? 'list'} {...(route.artifact_id === undefined ? {} : { artifactId: route.artifact_id })} onOpen={(artifact_id) => navigate({ kind: 'forms', artifact_id })} onBack={() => navigate({ kind: 'forms' })} />;
  else if (route.kind === 'views') page = <ViewsStudioPage key={route.artifact_id ?? 'list'} {...(route.artifact_id === undefined ? {} : { artifactId: route.artifact_id })} onOpen={(artifact_id) => navigate({ kind: 'views', artifact_id })} onBack={() => navigate({ kind: 'views' })} />;
  else if (route.kind === 'workspace') page =<Empty label="Cet espace de travail arrive avec les phases suivantes." />;
  else {
    // Accueil (ADR-092 §5) : le tableau de bord désigné par la navigation, sinon l'accueil simple.
    const home = navigation.home;
    const simple = <HomePage navigation={navigation} name={displayName} onNavigate={navigate} />;
    page = home === undefined ? simple : <ViewPage key={`home:${home.view_ref}`} source={{ view_ref: home.view_ref }} onRecordChange={() => undefined} fallback={() => simple} />;
  }

  return (
    <div className="ay-root" style={style}>
      <AppServicesContext.Provider value={services}>
        <ToastProvider>
        <FullScreenContext.Provider value={setFullScreen}>
        <AppShell
          bare={fullScreen}
          navigation={navigation}
          route={route}
          onNavigate={navigate}
          context={experience.value.context}
          {...(userLabel === undefined ? {} : { userLabel })}
          {...(productName === undefined ? {} : { productName })}
          onSignOut={onSignOut}
          loadBadge={loadBadge}
        >
          {page}
        </AppShell>
        </FullScreenContext.Provider>
        </ToastProvider>
      </AppServicesContext.Provider>
    </div>
  );
}

type PublicPage = { kind: 'booking' } | { kind: 'interview' } | { kind: 'backoffice'; engagement_id?: string } | null;

/**
 * Pages servies sans session dans le POC : rendez-vous (`#/rdv` ou `/rdv`) et entretien
 * (`#/entretien`), publics par nature ; back-office (`#/ayumia`) en démonstration locale seulement,
 * à placer derrière la session et la permission `implementation.conduct` dès qu'il lira l'API.
 */
function publicPage(): PublicPage {
  const route = parseRoute(window.location.hash);
  if (route.kind === 'booking' || route.kind === 'interview' || route.kind === 'backoffice') return route;
  return /^\/rdv\/?$/.test(window.location.pathname) ? { kind: 'booking' } : null;
}

function usePublicRoute(): PublicPage {
  const [isPublic, setPublic] = useState<PublicPage>(publicPage);
  useEffect(() => {
    const onChange = (): void => setPublic(publicPage());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return isPublic;
}

/** `entitlements` : droits du tenant servis par `GET /auth/me` (ADR-148) ; `null` tant qu'ils ne sont pas lus. */
type AuthState = { kind: 'checking' } | { kind: 'signed_out' } | { kind: 'cookie'; label: string; entitlements: Entitlements } | { kind: 'token'; token: string };

/** Lien de premier mot de passe ou de réinitialisation (`#/password/setup?token=…`), lu à chaque changement du fragment. */
function usePasswordLink(): ReturnType<typeof passwordLinkOf> {
  const [link, setLink] = useState(() => passwordLinkOf(window.location.hash));
  useEffect(() => {
    const onChange = (): void => setLink(passwordLinkOf(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return link;
}

const DEV_TOKEN_SIGNIN = import.meta.env['VITE_AYUMIA_DEV_TOKEN_SIGNIN'] === 'true';

export function App({ fetch, devTokenSignIn = DEV_TOKEN_SIGNIN }: { fetch?: Fetch; devTokenSignIn?: boolean } = {}) {
  const [auth, setAuth] = useState<AuthState>(devTokenSignIn ? { kind: 'signed_out' } : { kind: 'checking' });
  const session = auth.kind === 'token' ? auth.token : null;
  const isPublic = usePublicRoute();
  const passwordLink = usePasswordLink();
  const authClient = useMemo(() => new AuthClient(fetch === undefined ? {} : { fetch }), [fetch]);
  // Habillage public de la connexion (ADR-092 §7), lu sans session ; en échec, le défaut local.
  const loadPresentation = useCallback(() => fetchSignInPresentation(authClient), [authClient]);
  const signedIn = useCallback((outcome: AuthenticationOutcome) => setAuth({ kind: 'cookie', label: outcome.identity?.display_name ?? '', entitlements: outcome.entitlements ?? null }), []);
  // Après une connexion, les droits du tenant viennent de « qui suis-je » (ADR-148) ; sans réponse, le serveur seul décide.
  const entitlementsUnknown = auth.kind === 'cookie' && auth.entitlements === null;
  useEffect(() => {
    if (!entitlementsUnknown) return;
    let live = true;
    authClient
      .me()
      .then((outcome) => live && setAuth((current) => (current.kind === 'cookie' ? { ...current, entitlements: outcome.entitlements ?? [] } : current)))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [entitlementsUnknown, authClient]);
  // Session déjà ouverte (cookie) : un rechargement la retrouve sans redemander le mot de passe.
  useEffect(() => {
    if (auth.kind !== 'checking' || isPublic !== null || passwordLink !== null) return;
    let live = true;
    authClient
      .me()
      .then((outcome) => live && signedIn(outcome))
      .catch(() => live && setAuth({ kind: 'signed_out' }));
    return () => {
      live = false;
    };
  }, [auth.kind, authClient, isPublic, passwordLink, signedIn]);
  // Service simulé tant que la route publique de l'API n'existe pas (plan §13.3) : aucun appel réseau.
  // Démonstration : magasin en mémoire partagé par le rendez-vous, l'entretien et le back-office.
  const store = useMemo(() => {
    const created = new ImplementationStore();
    seedDemoStore(created, new Date());
    return created;
  }, []);
  const bookingService = useMemo(() => createMockBookingService({ latency_ms: 250, store }), [store]);
  // Moteur d'entretien de démonstration : scénario fictif, en attendant le moteur réel (plan §3.3).
  const interviewEngine = useMemo(
    () =>
      createDemoInterviewEngine({
        latency_ms: 900,
        onChange: (state) =>
          store.update(DEMO_ENGAGEMENT_ID, (record) => ({
            ...record,
            interview: state,
            consultant_requested: record.consultant_requested || state.consultant_present,
            status: state.status === 'review' ? 'completed' : state.messages.some((message) => message.role === 'customer') ? 'in_progress' : record.status,
          })),
      }),
    [store],
  );
  const client = useMemo(() => {
    const options: ConstructorParameters<typeof ApiClient>[0] = { session: () => session, onUnauthenticated: () => setAuth({ kind: 'signed_out' }) };
    if (fetch !== undefined) options.fetch = fetch;
    return new ApiClient(options);
  }, [session, fetch]);
  const defaultTheme = useMemo(() => applyThemes([]).variables as CSSProperties, []);
  if (isPublic !== null) {
    return (
      <div className="ay-root" style={defaultTheme}>
        {isPublic.kind === 'booking' ? (
          <BookingPage service={bookingService} />
        ) : isPublic.kind === 'interview' ? (
          <InterviewPage engine={interviewEngine} />
        ) : (
          <BackOfficePage store={store} {...(isPublic.engagement_id === undefined ? {} : { engagementId: isPublic.engagement_id })} />
        )}
      </div>
    );
  }
  if (passwordLink !== null) {
    return (
      <div className="ay-root" style={defaultTheme}>
        <PasswordResetPage
          client={authClient}
          purpose={passwordLink.purpose}
          token={passwordLink.token}
          onDone={() => {
            window.location.hash = '#/';
            setAuth({ kind: 'signed_out' });
          }}
        />
      </div>
    );
  }
  if (auth.kind === 'checking') return <div className="ay-root" style={defaultTheme}><Loading /></div>;
  if (auth.kind === 'signed_out') {
    return (
      <div className="ay-root" style={defaultTheme}>
        {devTokenSignIn ? <DevTokenSignIn onSession={(token) => setAuth({ kind: 'token', token })} /> : <SignInPage client={authClient} onSignedIn={signedIn} loadPresentation={loadPresentation} />}
      </div>
    );
  }
  const signOut = (): void => {
    // Déconnexion (ADR-091 §7) : la session est révoquée au serveur, pas seulement oubliée ici.
    if (auth.kind === 'cookie') void authClient.logout().catch(() => undefined).finally(() => setAuth({ kind: 'signed_out' }));
    else setAuth({ kind: 'signed_out' });
  };
  return <Workspace client={client} authClient={authClient} onSignOut={signOut} {...(auth.kind === 'cookie' && auth.label !== '' ? { userLabel: auth.label } : {})} entitlements={auth.kind === 'cookie' ? auth.entitlements : null} />;
}
